<template>
    <C1FormGroup>
        <div class="afu-flex afu-items-center afu-justify-between">
            <C1Label :label-for="id">
                {{ $_seekerFieldByCode('desired_occupations').label || label }}
            </C1Label>

            <C1WhatIsThis
                class="afu-mb-2"
                :primary-text="$t('label.what_is_this_occupation')"
            />
        </div>

        <C1ClsSelect
            :id="id"
            :options="occupationOptions"
            :placeholder="$t('label.occupation_placeholder')"
            :job-categories="jobCategories"
            :selected-occupations="$_desiredOccupations"
            :search-loading="searchLoading"
            :disabled="$_seekerFieldByCode('desired_occupations').read_only"
            @search="searchOccupations"
            @hit="onOccupationChange"
            @attach="onOccupationAttach"
        />

        <div
            v-if="selectedOccupations.length"
            class="afu-mt-2"
        >
            <C1Tag
                v-for="occupation in selectedOccupations"
                :key="occupation.id"
                :removable="!$_seekerFieldByCode('desired_occupations').read_only"
                @remove="detachOccupation(occupation)"
            >
                {{ occupation.name }}
            </C1Tag>
        </div>

        <div
            v-else
            class="af-no-result-found afu-text-lg afu-font-semibold afu-mt-5 afu-text-neutral-dark"
        >
            <slot name="noResultText">
                {{ noResultText }}
            </slot>
        </div>
    </C1FormGroup>
</template>

<script>
    import { mapState, mapActions, mapMutations, mapGetters } from 'vuex';
    import C1FormGroup from '@/components/C1Components/C1FormGroup';
    import C1Tag from '@/components/C1Components/C1Tag';
    import C1ClsSelect from '@/components/C1Components/C1ClsSelect';
    import C1Label from '@/components/C1Components/C1Label';
    import C1WhatIsThis from '@/components/C1WhatIsThis';

    export default {
        name: 'SeekerOccupations',

        components: { C1WhatIsThis, C1Label, C1ClsSelect, C1FormGroup, C1Tag },

        props: {
            isRequired: {
                type: Boolean,
                default: false,
            },
        },

        data() {
            return {
                id: `location_${this._uid}`,
                label: this.$t('label.desired_occupations'),
                selectedOccupations: [],
                jobCategories: [],
                occupationOptions: [],
                searchLoading: false,
            };
        },

        computed: {
            ...mapState({
                $_desiredOccupations: state => state.me.desiredOccupations,
            }),

            ...mapGetters('site', {
                $_seekerFieldByCode: 'seekerFieldByCode',
            }),

            noResultText() {
                return `No ${this.label.toLowerCase()} added.`;
            },
        },

        watch: {
            $_meWorkLocations: {
                handler() {
                    this.cloneLocations();
                },
                deep: true,
            },
        },

        created() {
            this.getInitData();
        },

        methods: {
            ...mapActions('me', {
                $_syncDesiredOccupation: 'syncDesiredOccupation',
                $_attachDesiredOccupation: 'attachDesiredOccupation',
                $_detachDesiredOccupation: 'detachDesiredOccupation',
            }),

            ...mapActions('occupation', {
                $_getSeekerOccupations: 'getSeekerOccupations',
            }),

            ...mapActions('classification', {
                $_getClassificationSuggestions: 'getClassificationSuggestions',
            }),

            ...mapMutations('me', {
                $_STORE_DESIRED_OCCUPATION: 'STORE_DESIRED_OCCUPATION',
            }),

            getInitData() {
                this.$_getSeekerOccupations().then(response => {
                    this.jobCategories = response.data;
                });

                this.selectedOccupations = JSON.parse(
                    JSON.stringify(this.$_desiredOccupations),
                );
            },

            onOccupationChange(occupations) {
                this.selectedOccupations = occupations;
            },

            onOccupationAttach(occupation) {
                const data = {
                    subclassification_id: occupation.id,
                    classification_id: occupation.category_id,
                    is_master: occupation.is_master,
                };

                this.$_attachDesiredOccupation(data);
            },

            onOccupationDetach(occupation) {
                const data = {
                    subclassification_id: occupation.id,
                    classification_id: occupation.category_id,
                };

                this.$_detachDesiredOccupation(data);
            },

            detachOccupation(occupation) {
                const idx = this.selectedOccupations.findIndex(
                    o =>
                        o.id === occupation.id &&
                        o.category_id === occupation.category_id,
                );
                this.selectedOccupations.splice(idx, 1);
                this.$_STORE_DESIRED_OCCUPATION(this.selectedOccupations);
                this.onOccupationDetach(occupation);
            },

            searchOccupations(keyword) {
                this.searchLoading = true;
                this.$_getClassificationSuggestions({
                    keyword,
                    exclude_self: true,
                }).then(response => {
                    this.occupationOptions = response.data;
                    this.searchLoading = false;
                });
            },
        },
    };
</script>
